/**
 * Applies a single cell edit to CPT table state with dirty tracking and session persistence.
 * @module updateTableDataState
 */
import { CptCodesTableRowModel } from "../../../../../data-model/cptCodesTableModel";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../../data-model/medicalPaOrdersModel";
import { hasRowValueChanged } from "./getComparableRowValues";
import { updateRowDataForDifferentKeys } from "./updateRowDataForDifferentKeys";

/**
 * When true, dirty tracking compares only saved cell values (status id,
 * dates, visits, filing mode/detail, etc.). When false, falls back to the
 * previous full-row `JSON.stringify` comparison. Flip this off to roll back
 * without reverting the saved-value extractor.
 */
const USE_SAVED_VALUE_DIRTY_CHECK = true;

/**
 * Updates one field on a row, recomputes dirty flags against `originalTableData`, and saves to context.
 *
 * @param rowIndex - Zero-based row index in the CPT table
 * @param fieldKey - Column key being edited
 * @param fieldValue - New cell value from the table control
 * @param generalOrderDetails - Order context forwarded to field reducers
 * @param authOnFileDetails - Auth-on-file context forwarded to field reducers
 * @param originalTableData - Baseline rows for change detection
 * @param setTableData - React state setter for all rows
 * @param setHasChanges - React state setter toggled from deep comparison
 * @param saveTableDataToContext - Persists the updated row array to session store
 * @param category - Healthcare facility `category` forwarded to field reducers
 */
export const updateTableDataState = (
  rowIndex: number,
  fieldKey: string,
  fieldValue: any,
  // singleMedicalPaOrder: MedicalPaOrder,
  generalOrderDetails: GeneralOrderDetails | null | undefined,
  authOnFileDetails: AuthOnFile | null | undefined,
  originalTableData: CptCodesTableRowModel[],
  setTableData: React.Dispatch<React.SetStateAction<CptCodesTableRowModel[]>>,
  setHasChanges: React.Dispatch<React.SetStateAction<boolean>>,
  saveTableDataToContext: (data: CptCodesTableRowModel[]) => void,
  category?: string | null,
): void => {
  setTableData((prevData: CptCodesTableRowModel[]) => {
    const updatedData = [...(prevData ?? [])];
    updatedData[rowIndex] = updateRowDataForDifferentKeys(
      fieldKey,
      updatedData[rowIndex],
      fieldValue,
      generalOrderDetails ?? ({} as GeneralOrderDetails | null | undefined),
      authOnFileDetails ?? ({} as AuthOnFile | null | undefined),
      category,
    );
    const hasAnyChanges = checkForTableChanges(updatedData, originalTableData);
    setHasChanges(hasAnyChanges);
    saveTableDataToContext(updatedData);

    return updatedData;
  });
};

/**
 * Checks if table data has changes compared to original.
 *
 * Uses {@link USE_SAVED_VALUE_DIRTY_CHECK} to choose between saved-value
 * comparison (ignores badge chrome, filing-info transition flags, and
 * error/editability bits) and the legacy full-row stringify.
 *
 * @param updatedData - The current table data after updates
 * @param originalTableData - The original table data to compare against
 * @returns true if any row has changes, false otherwise
 */
const checkForTableChanges = (
  updatedData: CptCodesTableRowModel[],
  originalTableData: CptCodesTableRowModel[],
): boolean => {
  return updatedData.some((row, index) => {
    const originalRow = originalTableData[index];
    if (!originalRow) return false;

    if (USE_SAVED_VALUE_DIRTY_CHECK) {
      return hasRowValueChanged(row, originalRow);
    }

    return Object.keys(row).some((key) => {
      const currentValue = row[key as keyof CptCodesTableRowModel];
      const originalValue = originalRow[key as keyof CptCodesTableRowModel];
      return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
    });
  });
};
